<script setup lang="ts">
import { ConfirmationPopup, formatDateOnly } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useDeleteAdditionalPaymentRequest } from '@/api'
import AddPlusIcon from '@/assets/icons/add-plus.svg?component'
import DeleteIcon from '@/assets/icons/delete.svg?component'
import SearchIcon from '@/assets/icons/search.svg?component'
import AdditionalPaymentForm from '@/components/Advertiser/AdditionalPaymentForm.vue'
import { formatMoneyForAdmin } from '@/lib/formatters/money.formatter'
import { notification } from '@/lib/helpers'
import type { AdvertiserLegacyType } from '@/lib/types/advertiser.types'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  advertiserId: number
  additionalPayments: AdvertiserLegacyType['additionalPayments']
  legacyAdminAppUrl: string
  currency: string
  isLoading: boolean
}>()
const emits = defineEmits(['reload'])
const deletedPayments = ref<number[]>([])
const isDeletePopupOpen = ref(false)
const isAdditionalPaymentFormOpen = ref(false)
const deleteId = ref<number | null>(null)
const { deletePayment: deletePaymentRequest, isLoading: isDeleteLoading, error: isDeleteError } = useDeleteAdditionalPaymentRequest()

const userSearchQuery = ref<string>('')
const payments = computed(() =>
  (props.additionalPayments || []).filter(
    (item) =>
      (item.title?.includes(userSearchQuery.value) || item.user?.username?.includes(userSearchQuery.value)) &&
      !deletedPayments.value.includes(item.id),
  ),
)
const deletePayment = async () => {
  await deletePaymentRequest(deleteId.value!)
  isDeletePopupOpen.value = false

  if (!isDeleteError.value) {
    deleteId.value = null
    emits('reload', true)
    notification.success('The payment was deleted successfully')
  }
}
const openDeleteCampaignPopup = (id: number) => {
  deleteId.value = id
  isDeletePopupOpen.value = true
}
const openAdditionalPaymentForm = () => {
  isAdditionalPaymentFormOpen.value = true
}
</script>
<template>
  <LoadingComponent v-if="isLoading" :size="64" />
  <template v-else-if="additionalPayments?.length">
    <BoxComponent :mb="24">
      <FlexComponent align="center" justify="space-between">
        <InputComponent v-model="userSearchQuery" placeholder="Search payments" size="small">
          <template #prefix>
            <el-icon :size="18">
              <SearchIcon />
            </el-icon>
          </template>
        </InputComponent>
        <ButtonComponent variant="primary" @click="openAdditionalPaymentForm">
          <template #iconLeft>
            <el-icon :size="24" color="var(--inverted)">
              <AddPlusIcon />
            </el-icon>
          </template>
          Add additional payment
        </ButtonComponent>
      </FlexComponent>
    </BoxComponent>
    <FlexComponent v-if="payments.length" direction="column" :gap="8">
      <BoxComponent v-for="payment in payments" :key="payment.id" :p="16" w="100%" class="advertiser-details-payments__list-item">
        <FlexComponent :gap="8" justify="space-between" align="center">
          <BoxComponent w="100%" overflow-hidden>
            <TypographyComponent variant="title-large" text-truncate white-space="nowrap">
              {{ payment.title || `Payment #${payment.id}` }}
            </TypographyComponent>
          </BoxComponent>
          <BoxComponent w="100%">
            <TypographyComponent variant="label-medium" text-align="center">Payment date</TypographyComponent>
            <TypographyComponent variant="title-large" text-align="center">
              {{ formatDateOnly(payment.date) }}
            </TypographyComponent>
          </BoxComponent>
          <BoxComponent w="100%">
            <TypographyComponent variant="label-medium" text-align="center">Invoice amount</TypographyComponent>
            <TypographyComponent variant="title-large" text-align="center">
              {{ formatMoneyForAdmin({ amount: payment.invoiceAmount, currency }, { allowZero: true }) }}
            </TypographyComponent>
          </BoxComponent>
          <BoxComponent w="100%" overflow-hidden>
            <TypographyComponent variant="label-medium" text-align="center">Creator</TypographyComponent>
            <a v-if="payment.user" :href="`${legacyAdminAppUrl}/#/users/${payment.user.id}`" class="advertiser-details-payments__username">
              <TypographyComponent variant="title-large" text-align="center" text-truncate>
                {{ payment.user.username }}
              </TypographyComponent>
            </a>
            <TypographyComponent v-else variant="title-large" text-align="center">-</TypographyComponent>
          </BoxComponent>
          <BoxComponent w="100%">
            <TypographyComponent variant="label-medium" text-align="center">Creator earning</TypographyComponent>
            <TypographyComponent variant="title-large" text-align="center">
              {{
                payment.userEarnings !== null && payment.userEarnings !== undefined
                  ? formatMoneyForAdmin({ amount: payment.userEarnings, currency }, { allowZero: true })
                  : '-'
              }}
            </TypographyComponent>
          </BoxComponent>
          <ButtonComponent
            variant="transparent"
            is-icon
            target="_blank"
            class="advertiser-details-payments__delete-button"
            @click="openDeleteCampaignPopup(payment.id)"
          >
            <DeleteIcon />
          </ButtonComponent>
        </FlexComponent>
      </BoxComponent>
    </FlexComponent>
    <BoxComponent v-else :mb="48">
      <TypographyComponent variant="body-large" color="var(--secondary-48)" text-align="center">
        No additional payments.
      </TypographyComponent>
    </BoxComponent>
  </template>
  <BoxComponent v-else :pl="16" :pr="16" :pt="48" :pb="48">
    <BoxComponent :mb="48">
      <FlexComponent align="center" direction="column" :gap="24">
        <TypographyComponent variant="body-large" color="var(--secondary-48)" text-align="center">
          No additional payments yet.
        </TypographyComponent>
        <ButtonComponent variant="primary" @click="openAdditionalPaymentForm"> Add additional payment </ButtonComponent>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>
  <ConfirmationPopup
    v-model="isDeletePopupOpen"
    :is-loading="isDeleteLoading"
    title="Delete additional payment"
    description="Are you sure you want to delete this payment?"
    @on-submit="deletePayment"
  />
  <AdditionalPaymentForm
    v-model="isAdditionalPaymentFormOpen"
    :currency="currency"
    :advertiser-id="advertiserId"
    @handle-submit-success="emits('reload', true)"
  />
</template>

<style scoped lang="scss">
:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}

.advertiser-details-payments__username {
  &:hover {
    text-decoration: underline;
  }
}
.advertiser-details-payments__list-item {
  border-radius: 16px;
  border: 1px solid var(--button-hover);
  background: var(--inverted);

  &:hover {
    background: var(--secondary-16);
  }
}
.advertiser-details-payments__delete-button {
  &:hover {
    background: var(--button-hover);
  }

  :deep(svg path) {
    fill: var(--primary);
    transition: fill var(--transition) ease;
  }

  &:hover :deep(svg path) {
    fill: var(--error);
  }
}
</style>
